import { Injectable } from '@angular/core';
import { HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { NEVER, Observable, throwError as observableThrowError } from 'rxjs';
import { tap, switchMap, catchError, delay } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { GlobalLoaderService } from '../services/global-loader.service';
import { isNullOrUndefinedOrBlank } from '../utils/is-null-or-undefined';

@Injectable()
export class HttpAuthHeaderInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private router: Router,
    private globalLoader: GlobalLoaderService,
  ) {}

  public addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    const authHeader = this.authService.getAuthorizationHeader();
    if (!isNullOrUndefinedOrBlank(authHeader)) {
      const updatedReq = request.clone({
        headers: request.headers.set('Authorization', this.authService.getAuthorizationHeader())
      }) as HttpRequest<any>;

      // For debugging -
      // console.log(updatedReq);
      return updatedReq;
    }

    // No headers
    return request.clone();
  }

  /**
   * Both callers hand the request to this and then return NEVER, so the response is never
   * delivered and GlobalLoaderInterceptor's finalize - the only thing that lowers the loader
   * count - never runs. That left the spinner up over the login page until the user refreshed
   * by hand, which is what sent Bug 47900 back a second time.
   *
   * Clearing it here rather than in the branches because the reason is the same in both, and
   * because it belongs with the decision it follows from: the session is over, so everything
   * still in flight is abandoned with it, not just this request.
   */
  public logout(): void {
    this.globalLoader.reset();
    this.router.navigate(['/logout'], { skipLocationChange: true });
  }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Handle request
    request = this.addAuthHeader(request);

    // Handle response
    return next
      .handle(request)
      .pipe(
        catchError(error => {
          // If there's no active Firebase session (e.g. logged out in another tab), redirect to logout.
          if (!this.authService.hasActiveFirebaseSession()) {
            if (error.status === 401) {
              this.logout();
              // Already handled — don't rethrow, or the global ErrorHandler's own
              // 401 redirect will race with this one. NEVER rather than EMPTY: see the
              // refresh branch below for why completing is its own failure.
              return NEVER;
            }
            return observableThrowError(error);
          }

          // Other statuses
          if (error.status !== 401) {
            return observableThrowError(error);
          }

          if (error.status === 422) {
            // Re-throw the error so the component can catch it
            return observableThrowError(() => error);
          }

          // 401 error means the token is expired.
          return this.authService.refreshToken()
            .pipe(
              catchError(() => {
                // A refresh that fails means the session is gone for good - most often
                // because it was revoked server-side, which the Firebase SDK has no way of
                // knowing until it tries. Removing a second factor does that, and so does an
                // administrator's two-factor reset, and in both cases this tab goes on
                // believing it is signed in until the next request (Bug 47900).
                //
                // Not rethrown: the rejection is a Firebase auth error rather than an
                // HttpErrorResponse, so ErrorHandlerService matches none of its cases and
                // falls through to /error - which is issued after the /logout started here
                // and therefore wins.
                //
                // NEVER rather than EMPTY, which was the first fix and did not survive
                // re-test. A route guard wraps its result in first(), so a source that
                // completes without emitting raises EmptyError and the navigation fails with
                // the same /error this was meant to prevent - StartupEssentialsGuard makes an
                // HTTP call on every entry to /main, so any navigation after the session was
                // revoked hit it. Nothing downstream can act on a failed refresh; hanging
                // until the logout navigation tears the caller down is the honest answer.
                this.logout();
                return NEVER;
              }),
              switchMap(() => {
                const retryRequest = this.addAuthHeader(request);
                // Delay is necessary so we don't request new token too many times on simult requests
                return next
                  .handle(retryRequest)
                  .pipe(delay(1000));
              })
            );
        }), // -- End catch handler
      ); // -- End Pipe
  }
}
